import React, { useContext } from "react";
import {
  btnStyle,
  managementInputStyle,
  managementModalStyle,
  StyledModal,
} from "pages/PatientOverview/styles";
import { Box, Button, CircularProgress, Grid, TextField } from "@mui/material";
import { icons } from "constants/icons";
import BatchAdminInvite from "pages/auth/BatchAdminInvite/BatchAdminInvite";
import { InviteResultModal } from "pages/auth/InviteResultModal";
import { useValidEmail } from "hooks/useValidEmail";
import { useAppSelector } from "hooks/reduxHooks";
import { selectCurrentClinician } from "store/ClinicianStore/selectors";
import { AuthContext } from "contexts/authContext";
import { USER_GROUPS } from "constants/roles";
import { PatientRepository } from "repositories/PatientRepository";
import EmailListModal from "pages/auth/EmailListModal";
import { createPatientOnInvitePromise } from "repositories/patientApi";

type Props = {
  isEnabled: boolean;
  setIsEnabled: any;
};

export const InviteModal = ({ isEnabled, setIsEnabled }: Props) => {
  const [open, setOpen] = React.useState(false);
  const [isError, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [mailModalOpen, setMailModalOpen] = React.useState(false);
  const {
    email: patientEmail,
    setEmail: setPatientEmail,
    emailIsValid: patientEmailIsValid,
  } = useValidEmail("");
  const currentClinician = useAppSelector(selectCurrentClinician);
  const authContext = useContext(AuthContext);

  const onInvitePatient = async (
    e: any,
    email?: string,
    avoidOpenModal?: boolean,
  ) => {
    e.stopPropagation();
    const patientEmailParsed = email || patientEmail;

    if (!patientEmailParsed || !currentClinician?._id) return;
    try {
      setLoading(true);

      // FIRST STEP: adding new Patient to the internal DB
      await createPatientOnInvitePromise({
        email: patientEmailParsed.toLowerCase(),
        clinicianId: currentClinician?._id,
        clinicId: currentClinician?.clinicId ?? "",
        role: [USER_GROUPS.PATIENT],
      });
      // SECOND STEP: adding user to Cognito userpool
      await authContext.signUpAdmin(patientEmailParsed.toLowerCase());

      // THIRD STEP: assigment specific role (Clinician / Patient)
      if (authContext.adminAddUserToGroup) {
        await authContext.adminAddUserToGroup(
          patientEmailParsed.toLowerCase(),
          USER_GROUPS.PATIENT,
        );
      }
      setLoading(false);
      if (avoidOpenModal) {
        return { hasError: false };
      }
      setError(false);
      setOpen(true);
      setPatientEmail("");
    } catch (error: any) {
      setLoading(false);

      const errMessage =
        error.response.data.message || error.message || "Unexpected error";
      if (avoidOpenModal) {
        return { hasError: true, errorMessage: errMessage };
      }
      setError(true);
      setOpen(true);
    }
  };

  return (
    <StyledModal
      aria-labelledby="unstyled-modal-title"
      aria-describedby="unstyled-modal-description"
      open={isEnabled}
    >
      <Box sx={{ ...managementModalStyle, height: "fit-content" }}>
        <>
          <Grid container mt={1} mb={1}>
            <Grid item xs={11} sx={{ fontWeight: "700", fontSize: 16 }}>
              {"Invite Patient"}
            </Grid>
            <Grid item xs={1}>
              <img
                src={icons.modalClose}
                alt="close"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setIsEnabled(false);
                }}
                aria-hidden="true"
              />
            </Grid>
          </Grid>
          <Grid container mt={1} mb={1}>
            <Grid item xs={12} sx={{ fontSize: 12, fontWeight: "400" }}>
              Please add the email address of the patient you'd like to invite.
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                variant="standard"
                placeholder="Email"
                fullWidth
                required
                style={managementInputStyle}
                InputProps={{
                  disableUnderline: true,
                  style: { color: "#5C5D68" },
                }}
                onChange={(e: any) => setPatientEmail(e.target.value)}
              />
            </Grid>

            <Grid>
              <p
                style={{
                  width: "315px",
                  fontFamily: "SofiaPro",
                  fontSize: "16px",
                  fontWeight: "400",
                  color: "#5C5D68",
                  marginBottom: "11px",
                }}
              >
                or
              </p>
              <p
                style={{
                  width: "315px",
                  fontFamily: "SofiaPro",
                  fontSize: "16px",
                  fontWeight: "400",
                  color: "#5C5D68",
                  marginBottom: "11px",
                }}
              >
                You can copy/paste a the emails separated by comma.
              </p>
              <div
                aria-hidden="true"
                style={{
                  cursor: "pointer",
                  justifyContent: "left",
                  color: "white",
                  width: "120px",
                  ...btnStyle,
                }}
                onClick={() => {
                  setMailModalOpen(true);
                }}
              >
                <span>
                  <p>Paste emails</p>
                </span>
              </div>
            </Grid>

            <Grid container xs={12} justifyContent="flex-end" mt={3}>
              <Grid item>
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Button
                    title="Send Invite"
                    type="submit"
                    disabled={patientEmailIsValid != ""}
                    color="primary"
                    variant="contained"
                    style={btnStyle}
                    onClick={(e) => {
                      onInvitePatient(e);
                    }}
                  >
                    Send Invite
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
          <InviteResultModal
            extraWidth={Boolean(isError)}
            open={open}
            setOpen={setOpen}
            isError={isError}
          />

          <EmailListModal
            inviteFunction={onInvitePatient}
            setIsOpen={setMailModalOpen}
            isOpen={mailModalOpen}
          />
        </>
      </Box>
    </StyledModal>
  );
};
